import React from "react";
import { useTranslation } from "react-i18next";
import { useWizardState } from "../../hooks/useWizardState";
import { WizardStep } from "../../types/quote";
import clsx from "clsx";
import "./StepIndicator.css";

interface StepInfo {
  number: number;
  step: WizardStep;
  title: string;
}

const getSteps = (t: (key: string) => string): StepInfo[] => [
  { number: 1, step: "step1", title: t("steps.step1") },
  { number: 2, step: "step2", title: t("steps.step2") },
  { number: 3, step: "step3", title: t("steps.step3") },
  { number: 4, step: "step4", title: t("steps.step4") },
  { number: 5, step: "step5", title: t("steps.step5") },
];

const getStepNumber = (currentStep: WizardStep): number => {
  switch (currentStep) {
    case "step1":
      return 1;
    case "step2":
      return 2;
    case "step3":
      return 3;
    case "step4":
      return 4;
    case "step5":
      return 5;
    default:
      return 0;
  }
};

export const StepIndicator: React.FC = () => {
  const { t } = useTranslation();
  const { currentStep, goToStep } = useWizardState();
  const currentStepNumber = getStepNumber(currentStep);
  const STEPS = getSteps(t);

  const handleStepClick = (stepInfo: StepInfo) => {
    // Only allow navigation to completed steps or the current step
    const isCompleted = currentStepNumber > stepInfo.number;
    const isCurrent = currentStepNumber === stepInfo.number;
    
    // Also check if previous steps are valid (for going back)
    if (isCompleted) {
      // Can always go back to completed steps
      goToStep(stepInfo.step);
    } else if (isCurrent) {
      // Already on this step, no action needed
      return;
    }
    // Otherwise, step is in the future and not clickable (disabled)
  };

  return (
    <div className="step-indicator-container">
      <ol className="steps">
        {STEPS.map((stepInfo) => {
          const isCompleted = currentStepNumber > stepInfo.number;
          const isCurrent = currentStepNumber === stepInfo.number;
          const isUpcoming = currentStepNumber < stepInfo.number;
          const isClickable = isCompleted || isCurrent;

          return (
            <li
              key={stepInfo.number}
              className={clsx(
                "step",
                isCompleted ? "step-completed" : "step-upcoming"
              )}
            >
              <button
                type="button"
                onClick={() => handleStepClick(stepInfo)}
                disabled={!isClickable}
                className={clsx(
                  "step-header",
                  isCurrent && "step-current",
                  isCompleted && "step-completed",
                  isUpcoming && "step-upcoming",
                  !isClickable && "step-disabled"
                )}
                aria-label={`Go to step ${stepInfo.number}: ${stepInfo.title}`}
                aria-current={isCurrent ? "step" : undefined}
              >
                {isCompleted ? "✓" : stepInfo.number}
              </button>
              <h3 className="step-title">{stepInfo.title}</h3>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

